import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { formatDate, timeAgo } from "../lib/format.js";
import { PageSpinner, Modal, EmptyState } from "../components/ui.js";
import { QuickLogModal } from "../components/QuickLogModal.js";
import { IconHeart } from "../components/icons.js";

interface RecoveryStatus {
  inRecovery: boolean;
  phaseType: string | null;
  surgeryName: string | null;
  surgeryDate: string | null;
  daysSinceSurgery: number | null;
  restrictions: { key: string; limitValue: number | null; note: string | null }[];
  clearances: { scope: string; clearedAt: string }[];
}
interface Checkin {
  id: string;
  checkinDate: string;
  pain: number | null;
  energy: number | null;
  walkingComfort: number | null;
  notes: string | null;
  incisionState: string | null;
}
interface Walk {
  id: string;
  sessionDate: string;
  durationMinutes: number | null;
  steps: number | null;
}
interface RecoveryData {
  status: RecoveryStatus;
  recentCheckins: Checkin[];
  recentWalks: Walk[];
}

const RESTRICTION_LABELS: Record<string, string> = {
  no_resistance_training: "No resistance training",
  no_abdominal_exercise: "No abdominal / core work",
  no_strenuous_exercise: "No strenuous exercise",
  no_lifting_over_limit: "Lifting limit",
  no_high_impact: "No high-impact movement",
  no_aggressive_calorie_deficit: "No aggressive calorie deficit",
  walking_tolerance_only: "Walking as tolerated only"
};

const CLEARANCE_SCOPES = [
  ["general_exercise", "General exercise"],
  ["resistance_training", "Resistance training"],
  ["abdominal", "Abdominal / core work"],
  ["high_impact", "High-impact movement"],
  ["full", "Full clearance"]
] as const;

export function RecoveryPage() {
  const [data, setData] = useState<RecoveryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkinModal, setCheckinModal] = useState(false);
  const [walkModal, setWalkModal] = useState(false);
  const [clearanceModal, setClearanceModal] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.get<RecoveryData>("/api/recovery"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load recovery.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function exitRecovery() {
    try {
      await api.post("/api/recovery/exit");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not exit recovery.");
    }
  }

  if (!data) {
    return (
      <div>
        <div className="page-header">
          <h1>Recovery</h1>
        </div>
        {error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />}
      </div>
    );
  }

  const s = data.status;

  return (
    <div>
      <div className="page-header">
        <h1>Recovery</h1>
        {s.inRecovery ? (
          <p className="subtitle">
            {s.surgeryName ? `Recovering from ${s.surgeryName}.` : "Recovery Mode."} Healing first.
          </p>
        ) : (
          <p className="subtitle">You’re not currently in Recovery Mode.</p>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {s.inRecovery && (
        <>
          <div className="stat-grid">
            {s.daysSinceSurgery != null && (
              <div className="stat">
                <div className="stat-label">Since surgery</div>
                <div className="stat-value">
                  {s.daysSinceSurgery < 0 ? `in ${-s.daysSinceSurgery}d` : `Day ${s.daysSinceSurgery}`}
                </div>
              </div>
            )}
            {s.surgeryDate && (
              <div className="stat">
                <div className="stat-label">Surgery date</div>
                <div style={{ fontWeight: 600, marginTop: 6 }}>{formatDate(s.surgeryDate)}</div>
              </div>
            )}
          </div>

          <div className="row mt-4 wrap">
            <button className="btn btn-primary" onClick={() => setCheckinModal(true)}>
              <IconHeart width={16} height={16} /> Recovery check-in
            </button>
            <button className="btn" onClick={() => setWalkModal(true)}>
              Log a walk
            </button>
          </div>
        </>
      )}

      {/* Restrictions */}
      <div className="card mt-6">
        <div className="card-header">
          <span className="card-title">Current movement limits</span>
        </div>
        <p className="muted text-xs">
          These are enforced on every plan ClovaForm builds. They come from your
          clinician — ClovaForm never decides you’re cleared.
        </p>
        {s.restrictions.length === 0 ? (
          <p className="muted text-sm mt-4">No active movement restrictions.</p>
        ) : (
          <div className="table-cards mt-4">
            {s.restrictions.map((r) => (
              <div className="table-card row-between" key={r.key}>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {RESTRICTION_LABELS[r.key] ?? r.key}
                    {r.limitValue != null ? ` (${r.limitValue} lb)` : ""}
                  </div>
                  {r.note && <div className="dim text-xs">{r.note}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {s.inRecovery && (
        <div className="card mt-4">
          <div className="card-header">
            <span className="card-title">Medical clearance</span>
          </div>
          <p className="muted text-sm">
            When your clinician clears you for an activity, record it here. Only
            you can do this — elapsed time never unlocks exercise in ClovaForm.
          </p>
          <div className="row mt-4 wrap">
            <button className="btn" onClick={() => setClearanceModal(true)}>
              Record clearance
            </button>
            <button className="btn btn-ghost" onClick={exitRecovery}>
              Leave Recovery Mode
            </button>
          </div>
          {s.clearances.length > 0 && (
            <div className="mt-4 dim text-sm">
              Recorded: {s.clearances.map((c) => c.scope.replace("_", " ")).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="mt-6">
        <h2>Recovery timeline</h2>
        {data.recentCheckins.length === 0 && data.recentWalks.length === 0 ? (
          <EmptyState title="No entries yet" icon={<IconHeart />}>
            Your recovery check-ins and walks will build a timeline here.
          </EmptyState>
        ) : (
          <div className="stack mt-4">
            {data.recentCheckins.map((c) => (
              <div className="card" key={c.id}>
                <div className="row-between">
                  <span style={{ fontWeight: 600 }}>Recovery check-in</span>
                  <span className="dim text-sm">{timeAgo(c.checkinDate)}</span>
                </div>
                <div className="row mt-2 wrap text-sm">
                  {c.pain != null && <span className="badge badge-muted">Pain {c.pain}/10</span>}
                  {c.energy != null && <span className="badge badge-muted">Energy {c.energy}/10</span>}
                  {c.walkingComfort != null && <span className="badge badge-muted">Walking {c.walkingComfort}/10</span>}
                </div>
                {c.notes && <p className="muted text-sm mt-2">{c.notes}</p>}
                {c.incisionState && <p className="dim text-xs mt-2">Incision: {c.incisionState}</p>}
              </div>
            ))}
            {data.recentWalks.map((w) => (
              <div className="card" key={w.id}>
                <div className="row-between">
                  <span style={{ fontWeight: 600 }}>Walk</span>
                  <span className="dim text-sm">{timeAgo(w.sessionDate)}</span>
                </div>
                <div className="muted text-sm mt-1">
                  {[w.durationMinutes ? `${w.durationMinutes} min` : null, w.steps ? `${w.steps} steps` : null]
                    .filter(Boolean)
                    .join(" · ") || "Logged"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {checkinModal && (
        <QuickLogModal type="recovery" onClose={() => setCheckinModal(false)} onLogged={load} />
      )}
      {walkModal && <QuickLogModal type="walk" onClose={() => setWalkModal(false)} onLogged={load} />}
      {clearanceModal && (
        <ClearanceModal onClose={() => setClearanceModal(false)} onSaved={async () => { setClearanceModal(false); await load(); }} />
      )}
    </div>
  );
}

function ClearanceModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [scope, setScope] = useState("general_exercise");
  const [source, setSource] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/recovery/clearance", { scope, source: source || null });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save.");
      setSaving(false);
    }
  }

  return (
    <Modal title="Record medical clearance" onClose={onClose}>
      <div className="alert alert-info text-sm">
        Record this only when your clinician has actually cleared you. ClovaForm
        will release the matching limits — it never clears you on its own.
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="field">
        <label>What were you cleared for?</label>
        <select className="select" value={scope} onChange={(e) => setScope(e.target.value)}>
          {CLEARANCE_SCOPES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Source (optional)</label>
        <input className="input" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. 2-week follow-up" />
      </div>
      <button className="btn btn-primary btn-block mt-4" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Record clearance"}
      </button>
    </Modal>
  );
}
